export const EASY_READ_JOURNEYS = Object.freeze({
  'er-opd-7M4Q2V9K': {
    id: 'generic-easy-read-outpatient-appointment',
    accessKey: 'er-opd-7M4Q2V9K',
    status: 'draft',
    public: false,
    trustId: 'generic_nhs',
    trustName: 'NHS hospital',
    hospitalName: 'Outpatient appointment',
    title: 'Outpatient Appointment: Easy Read version',
    eyebrow: 'Easy Read Hospital Journey',
    introduction: 'This guide explains what may happen at a hospital appointment. It has short sections and pictures.',
    easyRead: true,
    lowCognitiveLoad: true,
    simplifiedRoles: true,
    exploreMore: {
      locationFilter: 'outpatients',
      title: 'Who might I meet?',
      description: 'You may meet other people too.',
      linkLabel: 'See more people you might meet',
    },
    roles: [
      { id: '698d909a646a0ce1fbd1e0d2', title: 'Receptionist', shortTitle: 'Receptionist', shortDescription: 'Checks you in and tells the clinic you have arrived.' },
      { id: '698d7005ac75e1a2a643d499', title: 'Staff Nurse', shortTitle: 'Nurse', shortDescription: 'May do health checks and help during your appointment.' },
      { id: '698d7005ac75e1a2a643d49c', title: 'Consultant', shortTitle: 'Doctor or specialist', shortDescription: 'Talks with you about your health and care.' },
      { id: '698db91853146ce9190809e6', title: 'Healthcare Assistant', shortTitle: 'Healthcare assistant', shortDescription: 'May help with simple health checks.' },
      { id: 'learning_disability_nurse', title: 'Registered Learning Disability Nurse', shortTitle: 'Learning disability nurse', shortDescription: 'Can help staff understand what help you need.' },
    ],
    stages: [
      {
        id: 'before-you-go',
        title: 'Before you go',
        description: 'You will get information about when and where to go.',
        easyReadImage: { src: '/easy-read/easy-read-outpatient-before-you-go.webp', alt: 'Easy Read picture: getting ready for an outpatient appointment', placeholder: false, label: 'Easy Read picture' },
        body: [
          'Read your appointment letter.',
          'You can ask someone you trust to read it with you.',
          'Bring your medicines or a medicines list if the hospital asks.',
          'Before the appointment, tell the hospital what helps you communicate.',
          'Tell them what help you need.',
        ],
        roleIds: ['learning_disability_nurse'],
        linkIds: ['preparing', 'support'],
        questionIndexes: [0, 1],
      },
      {
        id: 'arrive-and-check-in',
        title: 'Arrive and check in',
        description: 'Tell reception that you have arrived.',
        easyReadImage: { src: '/easy-read/easy-read-outpatient-arrive-and-check-in.webp', alt: 'Easy Read picture: checking in for an outpatient appointment', placeholder: false, label: 'Easy Read picture' },
        body: [
          'You may need to give your name, date of birth and address.',
          'A receptionist will tell you where to wait.',
          'Ask for help if the area is busy or noisy.',
          'Ask for help if you do not understand what to do.',
        ],
        roleIds: ['698d909a646a0ce1fbd1e0d2'],
        linkIds: ['where-to-go'],
        questionIndexes: [2],
      },
      {
        id: 'waiting',
        title: 'Waiting for your appointment',
        description: 'You may wait before someone calls your name.',
        easyReadImage: { src: '/easy-read/easy-read-outpatient-waiting.webp', alt: 'Easy Read picture: waiting for an outpatient appointment', placeholder: false, label: 'Easy Read picture' },
        body: [
          'Staff may check your blood pressure, weight or temperature.',
          'Tell staff if waiting is difficult for you.',
          'You can ask for a quieter place.',
          'You can ask what will happen next.',
        ],
        roleIds: ['698d7005ac75e1a2a643d499', '698db91853146ce9190809e6'],
        questionIndexes: [3, 4],
      },
      {
        id: 'your-appointment',
        title: 'Your appointment',
        description: 'A doctor, nurse or other staff member will talk with you.',
        easyReadImage: { src: '/easy-read/easy-read-outpatient-your-appointment.webp', alt: 'Easy Read picture: talking with a clinician at an outpatient appointment', placeholder: false, label: 'Easy Read picture' },
        body: [
          'They may ask how you have been feeling.',
          'They may examine you.',
          'They may talk about tests or treatment.',
          'Ask them to explain anything you do not understand.',
          'You can ask someone you trust to stay with you.',
        ],
        roleIds: ['698d7005ac75e1a2a643d49c', '698d7005ac75e1a2a643d499'],
        questionIndexes: [5, 6],
      },
      {
        id: 'after-your-appointment',
        title: 'After your appointment',
        description: 'The team will tell you what happens next.',
        easyReadImage: { src: '/easy-read/easy-read-outpatient-after-your-appointment.webp', alt: 'Easy Read picture: leaving the outpatient department after an appointment', placeholder: false, label: 'Easy Read picture' },
        body: [
          'You may need another appointment, a test or a new medicine.',
          'Ask staff to write down the plan if that helps.',
          'Make sure you know who to contact if you are worried.',
        ],
        linkIds: ['after'],
        questionIndexes: [7],
      },
    ],
    usefulLinks: [
      { id: 'where-to-go', title: 'Where to go', description: 'Directions and information about where to go.', url: null },
      { id: 'preparing', title: 'Getting ready for your appointment', description: 'What to bring and what to do before you come.', url: null },
      { id: 'support', title: 'Support you can ask for', description: 'Ask about help with communication, noise, light or waiting.', url: null },
      { id: 'after', title: 'What happens after your appointment', description: 'Information about results, medicines and later appointments.', url: null },
    ],
    questions: [
      'Can I bring someone with me?',
      'Can you write down what helps me communicate?',
      'Can you show me where I need to wait?',
      'Can I wait somewhere quieter?',
      'What will happen next?',
      'Can you explain this in a different way?',
      'Can you write down the plan for me?',
      'Who should I contact if I have questions after I leave?',
    ],
    disclaimer: 'This Easy Read guide gives general information. Your appointment may be different. Follow the instructions from your hospital team. Ask if you need information explained another way.',
  },

  'er-scan-5R8N3C6T': {
    id: 'generic-easy-read-scan',
    accessKey: 'er-scan-5R8N3C6T',
    status: 'draft',
    public: false,
    trustId: 'generic_nhs',
    trustName: 'NHS hospital',
    hospitalName: 'Scan department',
    title: 'Scan: Easy Read version',
    eyebrow: 'Easy Read Hospital Journey',
    introduction: 'This guide explains what may happen when you have a scan. Different scans can feel and sound different.',
    easyRead: true,
    lowCognitiveLoad: true,
    simplifiedRoles: true,
    exploreMore: {
      locationFilter: 'diagnostics',
      title: 'Who might I meet?',
      description: 'You may meet other people too.',
      linkLabel: 'See more people you might meet',
    },
    roles: [
      { id: 'radiographer', title: 'Diagnostic Radiographer', shortTitle: 'Radiographer', shortDescription: 'A radiographer is a staff member who takes scans.' },
      { id: 'imaging_assistant', title: 'Imaging Assistant', shortTitle: 'Scan assistant', shortDescription: 'Helps you get ready for your scan.' },
      { id: 'hospital_porter', title: 'Hospital Porter', shortTitle: 'Porter', shortDescription: 'May help you move to the scan department.' },
      { id: 'learning_disability_nurse', title: 'Registered Learning Disability Nurse', shortTitle: 'Learning disability nurse', shortDescription: 'Can help staff understand what help you need.' },
    ],
    stages: [
      {
        id: 'before-your-scan',
        title: 'Before your scan',
        description: 'Your appointment information will tell you how to get ready.',
        easyReadImage: { src: '/easy-read/easy-read-scan-before-your-scan.webp', alt: 'Easy Read picture: getting ready for a hospital scan', placeholder: false, label: 'Easy Read picture' },
        body: [
          'Some scans need you to get ready in a special way.',
          'Follow the instructions from the hospital.',
          'Tell the hospital if you need more time or a quieter place.',
          'Tell them if you need help understanding what will happen.',
          'Bring anything the hospital asks you to bring.',
        ],
        roleIds: ['learning_disability_nurse'],
        linkIds: ['prepare', 'support'],
        questionIndexes: [0, 1],
      },
      {
        id: 'arrive',
        title: 'Arrive at the scan department',
        description: 'Check in and wait for a member of staff.',
        easyReadImage: { src: '/easy-read/easy-read-scan-arrive-at-imaging.webp', alt: 'Easy Read picture: checking in at the imaging department', placeholder: false, label: 'Easy Read picture' },
        body: [
          'Tell reception that you have arrived.',
          'You may need to wait until the scan room is ready.',
          'Tell staff if you feel worried or need help while you wait.',
        ],
        linkIds: ['where'],
        questionIndexes: [2],
      },
      {
        id: 'get-ready',
        title: 'Get ready for the scan',
        description: 'A member of staff will explain what you need to do.',
        easyReadImage: { src: '/easy-read/easy-read-scan-get-ready.webp', alt: 'Easy Read picture: getting ready for a scan with a member of staff', placeholder: false, label: 'Easy Read picture' },
        body: [
          'You may need to change your clothes.',
          'You may need to remove metal items.',
          'For some scans, staff put a small plastic tube into your arm.',
          'They may use it to give you medicine or a special liquid.',
          'Tell staff if you are frightened or uncomfortable.',
          'Tell staff if you do not understand.',
        ],
        roleIds: ['radiographer', 'imaging_assistant'],
        questionIndexes: [3, 4],
      },
      {
        id: 'have-the-scan',
        title: 'Have the scan',
        description: 'Staff will help you get into the right position. They will tell you when the scan starts.',
        easyReadImage: { src: '/easy-read/easy-read-scan-have-the-scan.webp', alt: 'Easy Read picture: having a scan with a radiographer nearby', placeholder: false, label: 'Easy Read picture' },
        body: [
          'You may need to keep still for a short time.',
          'Some scanners are noisy. Staff may give you headphones or ear protection.',
          'Tell staff if you need to stop.',
          'Ask them to explain something again if you need to.',
        ],
        roleIds: ['radiographer'],
        questionIndexes: [5],
      },
      {
        id: 'after-the-scan',
        title: 'After the scan',
        description: 'Staff will tell you when you can leave.',
        easyReadImage: { src: '/easy-read/easy-read-scan-after-the-scan.webp', alt: 'Easy Read picture: leaving the imaging department after a scan', placeholder: false, label: 'Easy Read picture' },
        body: [
          'Most people go home or back to their ward after the scan.',
          'The radiographer may not be able to tell you the result.',
          'Ask when and how you will get the result.',
        ],
        linkIds: ['results'],
        questionIndexes: [6, 7],
      },
    ],
    usefulLinks: [
      { id: 'prepare', title: 'Getting ready for your scan', description: 'What to do before your scan.', url: null },
      { id: 'where', title: 'Where to go', description: 'Directions and information about the scan department.', url: null },
      { id: 'support', title: 'Support you can ask for', description: 'Ask about help with communication, noise, light or extra time.', url: null },
      { id: 'results', title: 'Getting your results', description: 'Information about how you will get your scan result.', url: null },
    ],
    questions: [
      'What scan am I having?',
      'Do I need to do anything before the scan?',
      'Can someone come with me?',
      'What will the scanner look or sound like?',
      'Will I need a small tube in my arm?',
      'Can you tell me before you touch or move me?',
      'When can I go home?',
      'How will I get my results?',
    ],
    disclaimer: 'This Easy Read guide gives general information. Different scans need different preparation. Follow the instructions sent by your hospital. Tell staff what help you need.',
  },

  'er-blood-2H7W5P9F': {
    id: 'generic-easy-read-blood-test',
    accessKey: 'er-blood-2H7W5P9F',
    status: 'draft',
    public: false,
    trustId: 'generic_nhs',
    trustName: 'NHS hospital',
    hospitalName: 'Blood test',
    title: 'Blood Test: Easy Read version',
    eyebrow: 'Easy Read Hospital Journey',
    introduction: 'This guide explains what may happen when you have a blood test.',
    easyRead: true,
    lowCognitiveLoad: true,
    simplifiedRoles: true,
    exploreMore: {
      locationFilter: 'diagnostics',
      title: 'Who might I meet?',
      description: 'You may meet other people too.',
      linkLabel: 'See more people you might meet',
    },
    roles: [
      { id: '7011ef9f674bd4d185ab5e5e', title: 'Phlebotomist', shortTitle: 'Blood test staff member', shortDescription: 'Takes a small amount of blood from your arm.' },
      { id: '698d7005ac75e1a2a643d499', title: 'Staff Nurse', shortTitle: 'Nurse', shortDescription: 'May take your blood or give you extra help.' },
      { id: '698db91853146ce9190809e6', title: 'Healthcare Assistant', shortTitle: 'Healthcare assistant', shortDescription: 'May help with your blood test.' },
      { id: 'learning_disability_nurse', title: 'Registered Learning Disability Nurse', shortTitle: 'Learning disability nurse', shortDescription: 'Can help plan what makes blood tests easier for you.' },
    ],
    stages: [
      {
        id: 'before-your-test',
        title: 'Before your blood test',
        description: 'Check if you need to do anything before your blood test.',
        easyReadImage: { src: '/easy-read/easy-read-blood-test-before-your-test.webp', alt: 'Easy Read picture: getting ready for a blood test', placeholder: false, label: 'Easy Read picture' },
        body: [
          'Most blood tests do not need you to do anything special.',
          'Some blood tests need you to stop eating or drinking first.',
          'Follow the instructions from your hospital.',
          'Tell staff if needles are difficult for you.',
          'Tell them what usually helps.',
        ],
        roleIds: ['learning_disability_nurse'],
        linkIds: ['prepare', 'support'],
        questionIndexes: [0, 1],
      },
      {
        id: 'arrive',
        title: 'Arrive and check in',
        description: 'Tell staff that you have arrived for your blood test.',
        easyReadImage: { src: '/easy-read/easy-read-blood-test-arrive-and-check-in.webp', alt: 'Easy Read picture: checking in for a blood test', placeholder: false, label: 'Easy Read picture' },
        body: [
          'You may need to wait until staff call your name.',
          'Tell staff if you need a quieter place.',
          'Tell staff if you need more time.',
          'You can ask someone you trust to stay with you.',
        ],
        linkIds: ['where'],
        questionIndexes: [2, 3],
      },
      {
        id: 'get-ready',
        title: 'Get ready',
        description: 'A staff member will explain what they are going to do.',
        easyReadImage: { src: '/easy-read/easy-read-blood-test-get-ready.webp', alt: 'Easy Read picture: getting ready for a blood test with a staff member', placeholder: false, label: 'Easy Read picture' },
        body: [
          'You will usually sit in a chair.',
          'You may be asked which arm is best to use.',
          'Tell staff if you want them to explain each step before they do it.',
        ],
        roleIds: ['7011ef9f674bd4d185ab5e5e', '698d7005ac75e1a2a643d499'],
        questionIndexes: [4],
      },
      {
        id: 'take-the-blood',
        title: 'The blood test',
        description: 'A small needle takes a little blood from your arm.',
        easyReadImage: { src: '/easy-read/easy-read-blood-test-the-blood-test.webp', alt: 'Easy Read picture: having blood taken from the arm', placeholder: false, label: 'Easy Read picture' },
        body: [
          'The needle may feel sharp for a short time.',
          'Your blood goes into one or more small tubes.',
          'Tell staff if you feel unwell.',
          'You can ask for a break.',
        ],
        roleIds: ['7011ef9f674bd4d185ab5e5e'],
        questionIndexes: [5],
      },
      {
        id: 'after-the-test',
        title: 'After the blood test',
        description: 'Staff will take the needle out. They will put cotton wool or a plaster on your arm.',
        easyReadImage: { src: '/easy-read/easy-read-blood-test-after-the-test.webp', alt: 'Easy Read picture: leaving after a blood test with a small plaster on the arm', placeholder: false, label: 'Easy Read picture' },
        body: [
          'You can usually leave soon after the test.',
          'Tell staff if you feel dizzy or unwell.',
          'Ask how and when you will get your results.',
        ],
        linkIds: ['results'],
        questionIndexes: [6],
      },
    ],
    usefulLinks: [
      { id: 'prepare', title: 'Getting ready for your blood test', description: 'What to do before your blood test.', url: null },
      { id: 'where', title: 'Where to go', description: 'Directions and information about where to go.', url: null },
      { id: 'support', title: 'Support you can ask for', description: 'Ask about extra time, lying down or a quieter place.', url: null },
      { id: 'results', title: 'Getting your results', description: 'Information about how and when you will get your results.', url: null },
    ],
    questions: [
      'Do I need to do anything before the blood test?',
      'Can you record what helps me with needles?',
      'Can someone stay with me?',
      'Can I wait somewhere quieter?',
      'Can you explain each step before you do it?',
      'Can we stop for a break if I need one?',
      'How will I get my results?',
    ],
    disclaimer: 'This Easy Read guide gives general information. Some blood tests need special preparation. Follow the instructions from your healthcare team. Tell staff what helps you.',
  },

  'er-ed-8D3K6R2N': {
    id: 'generic-easy-read-emergency-department',
    accessKey: 'er-ed-8D3K6R2N',
    status: 'draft',
    public: false,
    trustId: 'generic_nhs',
    trustName: 'NHS hospital',
    hospitalName: 'Emergency Department',
    title: 'Emergency Department: Easy Read version',
    eyebrow: 'Easy Read Hospital Journey',
    introduction: 'This guide explains what may happen in the Emergency Department. People who are very unwell may be seen first.',
    easyRead: true,
    lowCognitiveLoad: true,
    simplifiedRoles: true,
    exploreMore: {
      locationFilter: 'emergency',
      title: 'Who might I meet?',
      description: 'You may meet other people too.',
      linkLabel: 'See more people you might meet',
    },
    roles: [
      { id: '698d7005ac75e1a2a643d499', title: 'Staff Nurse', shortTitle: 'Nurse', shortDescription: 'Checks how you are and helps look after you.' },
      { id: 'resident_doctor_previously_referred_to_as_junior_doctor', title: 'Resident Doctor', shortTitle: 'Doctor', shortDescription: 'Checks you and helps decide what care you need.' },
      { id: '698db91853146ce9190809e6', title: 'Healthcare Assistant', shortTitle: 'Healthcare assistant', shortDescription: 'May help with health checks and care.' },
      { id: '698d7005ac75e1a2a643d4a1', title: 'Paramedic', shortTitle: 'Paramedic', shortDescription: 'May bring you to hospital in an ambulance.' },
      { id: 'learning_disability_nurse', title: 'Registered Learning Disability Nurse', shortTitle: 'Learning disability nurse', shortDescription: 'Can help staff understand what help you need.' },
    ],
    stages: [
      {
        id: 'arrive',
        title: 'Arrive at the Emergency Department',
        description: 'Tell staff why you have come to hospital.',
        easyReadImage: { src: '/easy-read/easy-read-ed-arrive.webp', alt: 'Easy Read picture: arriving at the Emergency Department reception', placeholder: false, label: 'Easy Read picture' },
        body: [
          'You may arrive alone, with someone you trust or in an ambulance.',
          'Staff will ask your name.',
          'They will ask what is wrong.',
          'Tell staff what helps you communicate.',
          'Tell staff what help you need.',
        ],
        roleIds: ['698d7005ac75e1a2a643d4a1', 'learning_disability_nurse'],
        linkIds: ['support'],
        questionIndexes: [0, 1],
      },
      {
        id: 'first-check',
        title: 'Your first health check',
        description: 'A nurse will check how unwell you are. They will decide what help you need first.',
        easyReadImage: { src: '/easy-read/easy-read-ed-first-health-check.webp', alt: 'Easy Read picture: having a first health check', placeholder: false, label: 'Easy Read picture' },
        body: [
          'They may check your temperature, pulse, blood pressure and oxygen.',
          'Tell them if you are in pain.',
          'Tell them about your medicines and allergies.',
          'Tell staff straight away if you feel worse.',
        ],
        roleIds: ['698d7005ac75e1a2a643d499', '698db91853146ce9190809e6'],
        questionIndexes: [2],
      },
      {
        id: 'wait',
        title: 'You may need to wait',
        description: 'People who need urgent care may be seen before you.',
        easyReadImage: { src: '/easy-read/easy-read-ed-wait.webp', alt: 'Easy Read picture: waiting in the Emergency Department', placeholder: false, label: 'Easy Read picture' },
        body: [
          'The Emergency Department can be busy, bright and noisy.',
          'Tell staff if waiting is difficult.',
          'Ask if you can wait somewhere quieter.',
          'Ask staff before you eat or drink.',
          'Ask staff before you take your usual medicines.',
          'Staff will tell you what is safe.',
        ],
        linkIds: ['waiting'],
        questionIndexes: [3, 4],
      },
      {
        id: 'tests-and-treatment',
        title: 'Tests and treatment',
        description: 'A doctor or nurse will talk with you. They will decide what care you need.',
        easyReadImage: { src: '/easy-read/easy-read-ed-tests-and-treatment.webp', alt: 'Easy Read picture: being examined or treated in the Emergency Department', placeholder: false, label: 'Easy Read picture' },
        body: [
          'You may have an examination.',
          'You may have a blood test or scan.',
          'You may be given medicine or other treatment.',
          'Ask staff to explain each step before it happens.',
        ],
        roleIds: ['resident_doctor_previously_referred_to_as_junior_doctor', '698d7005ac75e1a2a643d499'],
        questionIndexes: [5],
      },
      {
        id: 'what-happens-next',
        title: 'What happens next',
        description: 'You may go home. You may need more checks. You may need to stay in hospital.',
        easyReadImage: { src: '/easy-read/easy-read-ed-what-happens-next.webp', alt: 'Easy Read picture: being told what happens next before leaving the Emergency Department', placeholder: false, label: 'Easy Read picture' },
        body: [
          'Before you leave, ask what you should do at home.',
          'Make sure you know what warning signs to look out for.',
          'Ask who to contact if you get worse or are worried.',
        ],
        linkIds: ['leaving'],
        questionIndexes: [6, 7],
      },
    ],
    usefulLinks: [
      { id: 'support', title: 'Support you can ask for', description: 'Ask about help with communication, noise, light or waiting.', url: null },
      { id: 'waiting', title: 'Waiting in the Emergency Department', description: 'Why you may need to wait and what to do if you feel worse.', url: null },
      { id: 'leaving', title: 'Going home or staying in hospital', description: 'Information about what may happen next.', url: null },
    ],
    questions: [
      'Can someone stay with me?',
      'Can you record what helps me communicate and what support I need?',
      'What should I do if I feel worse while I am waiting?',
      'Can I wait somewhere quieter?',
      'Can I eat, drink or take my usual medicines?',
      'Can you explain what will happen before you do it?',
      'What do I need to do when I go home?',
      'Who should I contact if I get worse?',
    ],
    disclaimer: 'This Easy Read guide gives general information. Emergency care is different for each person. Tell staff straight away if you feel worse. Follow the instructions from the Emergency Department team.',
  },

  'er-ward-4V9C2M7Q': {
    id: 'generic-easy-read-hospital-stay',
    accessKey: 'er-ward-4V9C2M7Q',
    status: 'draft',
    public: false,
    trustId: 'generic_nhs',
    trustName: 'NHS hospital',
    hospitalName: 'Hospital ward',
    title: 'Hospital Stay: Easy Read version',
    eyebrow: 'Easy Read Hospital Journey',
    introduction: 'This guide explains what may happen when you stay in hospital.',
    easyRead: true,
    lowCognitiveLoad: true,
    simplifiedRoles: true,
    exploreMore: {
      locationFilter: 'wards',
      title: 'Who might I meet?',
      description: 'You may meet other people too.',
      linkLabel: 'See more people you might meet',
    },
    roles: [
      { id: '698d7005ac75e1a2a643d499', title: 'Staff Nurse', shortTitle: 'Nurse', shortDescription: 'Looks after you and gives you medicines.' },
      { id: 'resident_doctor_previously_referred_to_as_junior_doctor', title: 'Resident Doctor', shortTitle: 'Doctor', shortDescription: 'Checks your health and plans your care.' },
      { id: '698db91853146ce9190809e6', title: 'Healthcare Assistant', shortTitle: 'Healthcare assistant', shortDescription: 'Helps with health checks and everyday care.' },
      { id: 'learning_disability_nurse', title: 'Registered Learning Disability Nurse', shortTitle: 'Learning disability nurse', shortDescription: 'Helps staff understand what help you need.' },
      { id: '698d7005ac75e1a2a643d49e', title: 'Pharmacist', shortTitle: 'Pharmacist', shortDescription: 'Helps make sure your medicines are safe.' },
      { id: '698db91853146ce9190809ee', title: 'Physiotherapist', shortTitle: 'Physiotherapist', shortDescription: 'May help you move and get stronger.' },
    ],
    stages: [
      {
        id: 'arrive-on-the-ward',
        title: 'Arrive on the ward',
        description: 'A member of staff will show you where you will stay.',
        easyReadImage: { src: '/easy-read/easy-read-hospital-stay-arrive-on-the-ward.webp', alt: 'Easy Read picture: arriving on the hospital ward', placeholder: false, label: 'Easy Read picture' },
        body: [
          'A nurse will ask about your health and medicines.',
          'Tell staff how you communicate.',
          'Tell staff what help or changes you need.',
          'If you have a hospital passport, show it to staff.',
          'It tells staff important things about you.',
        ],
        roleIds: ['698d7005ac75e1a2a643d499', 'learning_disability_nurse'],
        linkIds: ['ward', 'support'],
        questionIndexes: [0, 1],
      },
      {
        id: 'daily-care',
        title: 'Your daily care',
        description: 'Staff will check how you are during the day and night.',
        easyReadImage: { src: '/easy-read/easy-read-hospital-stay-daily-care.webp', alt: 'Easy Read picture: a nurse checking blood pressure during daily care', placeholder: false, label: 'Easy Read picture' },
        body: [
          'They may check your temperature, pulse, blood pressure and oxygen.',
          'Nurses may give you medicines.',
          'They may help you with food or washing.',
          'Tell staff if you are in pain or worried.',
          'Tell staff if you need help.',
        ],
        roleIds: ['698d7005ac75e1a2a643d499', '698db91853146ce9190809e6'],
        questionIndexes: [2],
      },
      {
        id: 'talk-about-your-plan',
        title: 'Talk about your care',
        description: 'Doctors and nurses will check how you are. They will talk about what happens next.',
        easyReadImage: { src: '/easy-read/easy-read-hospital-stay-care-plan.webp', alt: 'Easy Read picture: talking about the care plan with a doctor and supporter', placeholder: false, label: 'Easy Read picture' },
        body: [
          'A doctor may visit you on the ward.',
          'They will talk with you about your care.',
          'You can ask questions.',
          'Ask staff to explain things again if you need to.',
          'Someone you trust can help you understand the plan.',
        ],
        roleIds: ['resident_doctor_previously_referred_to_as_junior_doctor', '698d7005ac75e1a2a643d499'],
        questionIndexes: [3, 4],
      },
      {
        id: 'tests-and-treatment',
        title: 'Tests and treatment',
        description: 'You may need tests, medicines or other treatment.',
        easyReadImage: { src: '/easy-read/easy-read-hospital-stay-tests-and-treatment.webp', alt: 'Easy Read picture: having an examination or treatment on the ward', placeholder: false, label: 'Easy Read picture' },
        body: [
          'Staff should explain what they want to do and why.',
          'Ask them to tell you before they touch or move you.',
          'Tell staff if you need a break.',
          'Ask them to explain it another way if you need to.',
        ],
        roleIds: ['698d7005ac75e1a2a643d49e', '698db91853146ce9190809ee'],
        questionIndexes: [5],
      },
      {
        id: 'visitors-and-support',
        title: 'Visitors and support',
        description: 'People you trust may visit or support you.',
        easyReadImage: { src: '/easy-read/easy-read-hospital-stay-visitors-and-support.webp', alt: 'Easy Read picture: a supporter visiting at the bedside', placeholder: false, label: 'Easy Read picture' },
        body: [
          'Ask the ward when people can visit.',
          'Tell staff if someone helps you communicate or feel safe.',
          'Ask who to speak to if you are worried about your care.',
        ],
        linkIds: ['visiting', 'support'],
        questionIndexes: [6],
      },
      {
        id: 'leaving-hospital',
        title: 'Leaving hospital',
        description: 'The team will plan when you can leave hospital.',
        easyReadImage: { src: '/easy-read/easy-read-hospital-stay-leaving-hospital.webp', alt: 'Easy Read picture: leaving the hospital ward with discharge information', placeholder: false, label: 'Easy Read picture' },
        body: [
          'Staff will tell you about your medicines.',
          'They will tell you about appointments you need later.',
          'Ask for information in a way you understand.',
          'Make sure you know who to contact if you feel unwell at home.',
        ],
        linkIds: ['discharge'],
        questionIndexes: [7],
      },
    ],
    usefulLinks: [
      { id: 'ward', title: 'About the ward', description: 'Information about the ward and what happens each day.', url: null },
      { id: 'support', title: 'Support you can ask for', description: 'Ask about help with communication or other things you need.', url: null },
      { id: 'visiting', title: 'Visitors and carers', description: 'Information about when people can visit and support you.', url: null },
      { id: 'discharge', title: 'Leaving hospital', description: 'Information about medicines, appointments and help after you leave.', url: null },
    ],
    questions: [
      'Who is my nurse today?',
      'Can you write down what helps me communicate?',
      'What is the plan for today?',
      'Which doctor is looking after me?',
      'Can you explain the plan in a different way?',
      'Can you tell me before you touch me or do a test?',
      'Can someone I trust support me on the ward?',
      'What do I need to know before I go home?',
    ],
    disclaimer: 'This Easy Read guide gives general information. Every hospital stay is different. Ask your ward team about your own care. Tell them what helps you understand and take part.',
  },
});

export const EASY_READ_JOURNEY_ACCESS_KEYS = Object.freeze({
  outpatientAppointment: 'er-opd-7M4Q2V9K',
  scan: 'er-scan-5R8N3C6T',
  bloodTest: 'er-blood-2H7W5P9F',
  emergencyDepartment: 'er-ed-8D3K6R2N',
  hospitalStay: 'er-ward-4V9C2M7Q',
});
